<template>
  <table class="w-100 bcl-token-list-table">
    <thead>
      <tr>
        <th class="cell-fake">
          <!-- Fake column that fixes ::before problem on rows -->
        </th>
        <th
          class="cell cell-rank text-body-2 opacity-50 text-left py-1 pl-2 pl-md-4"
        >
          <AppTooltip>
            <template #activator>
              {{ $t("common.rank") }}
            </template>
            {{ $t("common.rankInfo") }}
          </AppTooltip>
        </th>
        <th class="cell cell-name text-body-2 opacity-50 text-left py-1 px-3">
          <div v-text="$t('common.tokenName')" />
        </th>
        <th
          v-if="showCollectionColumn"
          class="cell cell-collection text-body-2 opacity-50 text-left text-md-right py-1 px-3"
        >
          <AppTooltip end>
            <template #activator>
              {{ $t("common.collection") }}
            </template>
            {{ $t("common.collectionDesc") }}
          </AppTooltip>
        </th>
        <th
          class="cell cell-price text-body-2 opacity-50 text-left text-md-right py-1 px-3"
        >
          {{ $t("common.price") }}
        </th>
        <th
          class="cell cell-market-cap text-body-2 opacity-50 text-right py-1 px-3"
        >
          {{ $t("common.marketCap") }}
        </th>
        <th
          class="cell cell-address text-body-2 opacity-50 text-right py-1 px-3"
        >
          {{ $t("common.contractAddress") }}
        </th>
        <th class="cell cell-chart text-body-2 opacity-50 text-right py-1 pl-3">
          {{ $t("common.performance") }}
        </th>
        <th class="cell-link"><!-- Links placeholder column --></th>
      </tr>
    </thead>

    <tbody class="token-list-skeleton" v-if="loading && !pages?.length">
      <TokenRowSkeleton v-for="n in 12" :key="n" />
    </tbody>

    <tbody v-else>
      <TokenListTableRow
        v-for="(token, index) in allItems"
        :key="index"
        :token="token"
        :show-collection-column="showCollectionColumn"
      />
    </tbody>
  </table>
</template>

<script lang="ts" setup>
import { computed, defineProps } from "vue";

import { useCommunityFactory } from "@/composables";

import TokenListTableRow from "./TokenListTableRow.vue";
import AppTooltip from "@/components/Common/AppTooltip.vue";
import TokenRowSkeleton from "@/components/Skeletons/TokenList/TokenRowSkeleton.vue";

const props = defineProps({
  pages: { type: Array, default: null },
  loading: { type: Boolean, default: false },
});

const { activeFactoryCollectionsNum } = useCommunityFactory();

const showCollectionColumn = computed(
  () => activeFactoryCollectionsNum.value > 1
);

const allItems = computed(() =>
  props.pages?.length ? props.pages.map((page) => page.items).flat() : []
);
</script>

<style lang="scss">
.bcl-token-list-table {
  border-collapse: separate;
  border-spacing: 0 8px;

  th {
    font-weight: bold;
  }

  @media screen and (min-width: 961px) {
    .cell {
      &-fake {
        width: 8px;
      }

      &-rank {
        width: 52px;
      }

      &-collection {
        width: 120px;
      }

      &-price,
      &-market-cap,
      &-address {
        width: 170px;
      }

      &-chart {
        width: 170px;

        .chart {
          max-width: 140px;
        }
      }

      &-name {
        // This column will take the remaining space
        width: auto;
        font-size: 21px;
      }

      &-link {
        width: 8px;
      }
    }
  }

  @media screen and (max-width: 960px) {
    & > thead {
      display: none;
    }

    & > tbody > tr {
      display: grid;
      grid-template-columns: 32px 5fr 2fr;
      grid-template-rows: 1fr 20px 20px;
      grid-template-areas:
        "rank name       chart"
        "rank price      chart"
        "rank market-cap chart";
      margin-top: 4px;
      padding-block: 8px;

      .cell {
        &-rank {
          grid-area: rank;
          padding-top: 3px;
          font-size: 12px;
        }

        &-name {
          grid-area: name;
        }

        &-price {
          grid-area: price;
        }

        &-market-cap {
          grid-area: market-cap;
        }

        &-price,
        &-market-cap {
          font-size: 0.814em;
        }

        &-chart {
          grid-area: chart;
        }

        &-collection,
        &-address {
          display: none;
        }
      }
    }
  }

  // On small screens put the chart below the token name
  @media screen and (max-width: 520px) {
    & > tbody > tr {
      grid-template-areas:
        "rank name       name"
        "rank price      chart"
        "rank market-cap chart";
    }
  }

  // On tiny screens hide the chart
  @media screen and (max-width: 370px) {
    .cell {
      &-chart {
        display: none;
      }
    }
  }
}
</style>
